// src/components/layout/PageShell.tsx
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackHomeButton from './BackHomeButton';
import LanguageSwitcher from '../language/LanguageSwitcher';
import SmartPageNavigation from '../navigation/SmartPageNavigation';

type PageShellProps = {
  children: React.ReactNode;
  title?: string;
  description?: string;
  className?: string;
  contentClassName?: string;
  showBackHome?: boolean;
  showLanguageSwitcher?: boolean;
  backHomeVariant?: 'floating' | 'inline';
  fullWidth?: boolean;
};

const PageShell: React.FC<PageShellProps> = ({
  children,
  title,
  description,
  className = '',
  contentClassName = '',
  showBackHome = true,
  showLanguageSwitcher = true,
  backHomeVariant = 'inline',
  fullWidth = false,
}) => {
  const location = useLocation();
  const { i18n } = useTranslation();

  const shouldShowInlineBackHome = showBackHome && backHomeVariant === 'inline';
  const shouldShowTopUtilityBar = shouldShowInlineBackHome || showLanguageSwitcher;
  const shouldShowFloatingBackHome = showBackHome && backHomeVariant === 'floating';

  useEffect(() => {
    const currentLanguage = i18n.language?.startsWith('en') ? 'en' : 'fr';

    if (typeof document !== 'undefined') {
      document.documentElement.lang = currentLanguage;

      if (title) {
        document.title = title;
      }

      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );

      if (description && metaDescription) {
        metaDescription.setAttribute('content', description);
      }

      if (description && !metaDescription) {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = description;
        document.head.appendChild(meta);
      }
    }
  }, [i18n.language, title, description]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [location.pathname]);

  return (
    <main
      className={[
        'min-h-screen bg-gradient-to-b from-white via-slate-50 to-white text-slate-900',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className={[
          fullWidth ? 'w-full' : 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8',
          'relative',
          contentClassName,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {shouldShowTopUtilityBar && (
          <div className="flex items-center justify-between gap-3 py-4">
            <div>
              {shouldShowInlineBackHome && (
                <BackHomeButton variant="inline" />
              )}
            </div>

            {showLanguageSwitcher && (
              <LanguageSwitcher compact className="shrink-0" />
            )}
          </div>
        )}

        {children}
      </div>

      {shouldShowFloatingBackHome && (
        <SmartPageNavigation variant="floating" />
      )}
    </main>
  );
};

export default PageShell;
